import React from 'react';
import { Dimensions, Text, View } from 'react-native';
import styles from '../screens/ProductsScreen.styles';
import { OwnershipScope } from '../types/favourites';
import { FavouriteToggleButton } from './FavouriteToggleButton';
import { PressableCard } from './PressableCard';

const { width } = Dimensions.get('window');
const isDesktop = width >= 1024;

interface ProductRowProps {
  isin: string;
  valor?: string;
  name: string;
  bid: number;
  ask: number;
  ccy: string;
  issuer?: string;
  initialFixing?: string;
  finalFixing?: string;
  redemptionDate?: string;
  productCode?: string;
  tradedNotional?: number;
  /** Layout compacto ISIN / Wof / distancia (low strike y near barrier desde Events) */
  isRiskTableLayout?: boolean;
  /** lowStrike: Strike % / Strike; barrier: Barrier % / Barrier + Type */
  riskReportKind?: 'lowStrike' | 'barrier';
  strikePct?: number;
  worstStrikeUnderlyingName?: string;
  worstStrikeUnderlyingWofPct?: number | null;
  worstStrikeUnderlyingDistancePct?: number | null;
  worstStrikeUnderlyingCurrent?: number | null;
  worstStrikeUnderlyingStrike?: number | null;
  worstBarrierUnderlyingName?: string;
  worstBarrierUnderlyingWofPct?: number | null;
  worstBarrierPct?: number | null;
  worstBarrierUnderlyingDistancePct?: number | null;
  worstBarrierUnderlyingCurrent?: number | null;
  worstBarrierUnderlyingBarrier?: number | null;
  barrierStyleName?: string | null;
  productTypeDisplay?: string;
  /** Listado: prioridad current notional; si no viene del API, usar traded */
  currentNotional?: number | null;
  // Event-related props
  showEventColumns?: boolean;
  showDistanceFromAutocallColumn?: boolean;
  /** Si true, se muestran columnas de Event Date / Event Type (solo para filtros de eventos, no para Expiry) */
  showEventDateTypeColumns?: boolean;
  nextEventDate?: string;
  eventType?: string;
  worstUnderlyingName?: string;
  distanceFromAutocall?: number;
  ownershipScope?: OwnershipScope;
  onPress?: () => void;
  abbreviateName: (name: string) => string;
  formatPrice: (price: number) => string;
  formatDate?: (date: string) => string;
}

export const ProductRow: React.FC<ProductRowProps> = ({
  isin,
  valor,
  name,
  bid,
  ask,
  ccy,
  issuer,
  initialFixing,
  finalFixing,
  redemptionDate,
  productCode,
  tradedNotional,
  isRiskTableLayout,
  riskReportKind,
  strikePct,
  worstStrikeUnderlyingName,
  worstStrikeUnderlyingWofPct,
  worstStrikeUnderlyingDistancePct,
  worstStrikeUnderlyingCurrent,
  worstStrikeUnderlyingStrike,
  worstBarrierUnderlyingName,
  worstBarrierUnderlyingWofPct,
  worstBarrierPct,
  worstBarrierUnderlyingDistancePct,
  worstBarrierUnderlyingCurrent,
  worstBarrierUnderlyingBarrier,
  barrierStyleName,
  productTypeDisplay,
  currentNotional,
  showEventColumns,
  showDistanceFromAutocallColumn = true,
  showEventDateTypeColumns,
  nextEventDate,
  eventType,
  worstUnderlyingName,
  distanceFromAutocall,
  ownershipScope = 'group',
  onPress,
  abbreviateName,
  formatPrice,
  formatDate,
}) => {
  const listNotional =
    currentNotional != null && Number.isFinite(Number(currentNotional))
      ? Number(currentNotional)
      : tradedNotional;
  const eventTypeParts = (eventType ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const rowHasAutocall = eventTypeParts.includes('Autocall');
  const rowHasIssuerCall = eventTypeParts.includes('Issuer Call');

  const showAutocallDistanceValue =
    rowHasAutocall &&
    distanceFromAutocall !== undefined &&
    distanceFromAutocall !== null;

  const favouriteControl = (
    <View style={styles.favouriteColumn}>
      <FavouriteToggleButton isin={isin} ownershipScope={ownershipScope} size="sm" />
    </View>
  );

  return (
    <PressableCard style={styles.productRow} onPress={onPress} pressedScale={0.98}>
      {isDesktop && isRiskTableLayout && riskReportKind === 'barrier' ? (
        <>
          {favouriteControl}
          <View style={styles.isinColumn}>
            <Text style={styles.isinText}>{isin || '-'}</Text>
          </View>

          <View style={styles.nameColumn}>
            <Text style={styles.nameText} numberOfLines={0}>
              {abbreviateName(name)}
            </Text>
          </View>

          <View style={styles.finalFixingColumn}>
            <Text style={styles.finalFixingText}>
              {formatDate && finalFixing ? formatDate(finalFixing) : '-'}
            </Text>
          </View>

          <View style={styles.wofUnderlyingColumn}>
            <Text style={styles.wofUnderlyingText}>{worstBarrierUnderlyingName || '-'}</Text>
          </View>

          <View style={styles.wofPctColumn}>
            <Text style={styles.wofPctText}>
              {worstBarrierUnderlyingWofPct === null || worstBarrierUnderlyingWofPct === undefined
                ? '-'
                : `${worstBarrierUnderlyingWofPct.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.strikePctColumn}>
            <Text style={styles.strikePctText}>
              {worstBarrierPct === null || worstBarrierPct === undefined
                ? '-'
                : `${worstBarrierPct.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.distancePctColumn}>
            <Text
              style={[
                styles.distancePctText,
                (worstBarrierUnderlyingDistancePct ?? 0) < 0 && styles.negativeNumber,
              ]}
            >
              {worstBarrierUnderlyingDistancePct === null ||
              worstBarrierUnderlyingDistancePct === undefined
                ? '-'
                : `${worstBarrierUnderlyingDistancePct.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.currentColumn}>
            <Text style={styles.currentText}>
              {worstBarrierUnderlyingCurrent === null || worstBarrierUnderlyingCurrent === undefined
                ? '-'
                : `${worstBarrierUnderlyingCurrent.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.strikeColumn}>
            <Text style={styles.strikeText}>
              {worstBarrierUnderlyingBarrier === null || worstBarrierUnderlyingBarrier === undefined
                ? '-'
                : `${worstBarrierUnderlyingBarrier.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.riskTableTypeColumn}>
            <Text style={styles.riskTableTypeText} numberOfLines={2}>
              {barrierStyleName || 'No barrier'}
            </Text>
          </View>

          <View style={styles.bidColumn}>
            <Text style={[styles.bidText, bid > 0 ? styles.pricePositive : styles.priceNeutral]}>
              {formatPrice(bid)}
            </Text>
          </View>
        </>
      ) : isDesktop && isRiskTableLayout ? (
        <>
          {favouriteControl}
          <View style={styles.isinColumn}>
            <Text style={styles.isinText}>{isin || '-'}</Text>
          </View>

          <View style={styles.nameColumn}>
            <Text style={styles.nameText} numberOfLines={0}>
              {abbreviateName(name)}
            </Text>
          </View>

          <View style={styles.finalFixingColumn}>
            <Text style={styles.finalFixingText}>
              {formatDate && finalFixing ? formatDate(finalFixing) : '-'}
            </Text>
          </View>

          <View style={styles.wofUnderlyingColumn}>
            <Text style={styles.wofUnderlyingText}>{worstStrikeUnderlyingName || '-'}</Text>
          </View>

          <View style={styles.wofPctColumn}>
            <Text style={styles.wofPctText}>
              {worstStrikeUnderlyingWofPct === null || worstStrikeUnderlyingWofPct === undefined
                ? '-'
                : `${worstStrikeUnderlyingWofPct.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.strikePctColumn}>
            <Text style={styles.strikePctText}>
              {strikePct === null || strikePct === undefined ? '-' : `${strikePct.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.distancePctColumn}>
            <Text
              style={[
                styles.distancePctText,
                (worstStrikeUnderlyingDistancePct ?? 0) < 0 && styles.negativeNumber,
              ]}
            >
              {worstStrikeUnderlyingDistancePct === null || worstStrikeUnderlyingDistancePct === undefined
                ? '-'
                : `${worstStrikeUnderlyingDistancePct.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.currentColumn}>
            <Text style={styles.currentText}>
              {worstStrikeUnderlyingCurrent === null || worstStrikeUnderlyingCurrent === undefined
                ? '-'
                : `${worstStrikeUnderlyingCurrent.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.strikeColumn}>
            <Text style={styles.strikeText}>
              {worstStrikeUnderlyingStrike === null || worstStrikeUnderlyingStrike === undefined
                ? '-'
                : `${worstStrikeUnderlyingStrike.toFixed(2)}`}
            </Text>
          </View>

          <View style={styles.bidColumn}>
            <Text style={[styles.bidText, bid > 0 ? styles.pricePositive : styles.priceNeutral]}>
              {formatPrice(bid)}
            </Text>
          </View>
        </>
      ) : (
        <>
          {favouriteControl}
          {isDesktop && showEventDateTypeColumns && (
            <View style={styles.eventDateColumn}>
              <Text style={styles.eventDateText}>
                {formatDate && nextEventDate ? formatDate(nextEventDate) : '-'}
              </Text>
            </View>
          )}

          {isDesktop && showEventDateTypeColumns && (
            <View style={styles.eventTypeColumn}>
              <Text style={styles.eventTypeText}>{eventType || '-'}</Text>
            </View>
          )}

          <View style={styles.isinColumn}>
            <Text style={styles.isinText}>{isin || '-'}</Text>
          </View>
      {isDesktop && (
        <View style={styles.valorColumn}>
          <Text style={styles.valorText}>{valor || '-'}</Text>
        </View>
      )}
      <View style={styles.nameColumn}>
        <Text style={styles.nameText} numberOfLines={0}>
          {abbreviateName(name)}
        </Text>
      </View>
      
      {/* Worst Of - only show when showEventColumns is true */}
      {isDesktop && showEventColumns && (
        <View style={styles.worstOfColumn}>
          <Text style={styles.worstOfText}>{worstUnderlyingName || '-'}</Text>
        </View>
      )}
      
      {/* Distance from Autocall - optional for specific event types */}
      {isDesktop && showEventColumns && showDistanceFromAutocallColumn && (
        <View style={styles.distanceColumn}>
          <Text style={styles.distanceText}>
            {rowHasIssuerCall
              ? 'Issuer call'
              : showAutocallDistanceValue
                ? `${distanceFromAutocall!.toFixed(2)}%`
                : '-'}
          </Text>
        </View>
      )}
      
      <View style={styles.ccyColumn}>
        <Text style={styles.ccyText}>{ccy}</Text>
      </View>
      {isDesktop && (
        <View style={styles.issuerColumn}>
          <Text style={styles.issuerText}>{issuer || '-'}</Text>
        </View>
      )}
      {isDesktop && !showEventColumns && (
        <View style={styles.fixingColumn}>
          <Text style={styles.fixingText}>{formatDate && initialFixing ? formatDate(initialFixing) : '-'}</Text>
        </View>
      )}
      {isDesktop && !showEventColumns && (
        <View style={styles.fixingColumn}>
          <Text style={styles.fixingText}>{formatDate && finalFixing ? formatDate(finalFixing) : '-'}</Text>
        </View>
      )}
      {isDesktop && !showEventColumns && (
        <View style={styles.dateColumn}>
          <Text style={styles.dateText}>{formatDate && redemptionDate ? formatDate(redemptionDate) : '-'}</Text>
        </View>
      )}
      <View style={styles.bidColumn}>
        <Text style={[styles.bidText, bid > 0 ? styles.pricePositive : styles.priceNeutral]}>
          {formatPrice(bid)}
        </Text>
      </View>
      <View style={styles.askColumn}>
        <Text style={[styles.askText, ask > 0 ? styles.pricePositive : styles.priceNeutral]}>
          {formatPrice(ask)}
        </Text>
      </View>
      <View style={styles.fixingColumn}>
        <Text style={styles.fixingText}>
          {listNotional != null && Number.isFinite(listNotional)
            ? ` ${listNotional.toLocaleString('en-US')}`
            : '-'}
        </Text>
      </View>
      
      {!isDesktop && <Text style={styles.arrowIcon}>›</Text>}
        </>
      )}
    </PressableCard>
  );
};


